import { ReactNode } from 'react';
import {
  IconBuildingChurch,
  IconCalendar,
  IconChecklist,
  IconFolders,
  IconShoppingCart,
  IconUsers,
  IconUser,
  IconAddressBook,
  IconLayoutDashboard,
  IconListDetails,
  IconListCheck,
  IconUserShield,
  IconComponents,
  IconPalette,
  IconTypography,
  IconForms,
  IconCar,
  IconTable,
  IconLanguage,
  IconMoon,
  IconSquare,
  IconDatabase,
  IconSettings,
  IconSparkles,
  IconClick,
  IconArrowsMove,
  IconBriefcase,
  IconAccessible,
  IconBell,
  IconEdit,
  IconLayoutGrid,
  IconApps,
  IconUserCircle,
  IconFolder,
  IconChartBar,
  IconMessage,
  IconReceipt,
  IconHeadset,
  IconLock,
  IconFileText,
  IconServer,
  IconLogin,
  IconUserPlus,
  IconKey,
  IconLockCheck,
  IconDeviceMobile,
  IconDeviceDesktop,
  IconDots,
  IconSend,
  IconChartLine,
  IconGauge,
  IconAlertCircle,
  IconBrain,
  IconChartArea,
  IconChartPie,
  IconTableOptions,
  IconChartDots,
  IconMapPin,
  IconHierarchy,
  IconMessageCircle,
  IconBooks,
  IconBrandFacebook,
  IconBuildingStore,
  IconShoppingBag,
  IconSpeakerphone,
  IconCalendarEvent,
  IconBox,
  IconQrcode,
  IconShieldCheck,
  IconFlag,
  IconBrandWhatsapp,
  IconMail,
  IconScan,
  IconClipboardList,
  IconArrowsExchange,
  IconFileImport,
  IconReportAnalytics,
  IconWallet,
  IconPlugConnected,
  IconWebhook,
  IconCategory,
  IconBolt,
  IconBulb,
  IconTags,
  IconHelp,
  IconPhoneCall,
  IconRoute,
  IconPhoneOff,
  IconArrowForwardUp,
  IconHeartHandshake,
  IconUsersGroup,
} from '@tabler/icons-react';

export type DomainKey =
  | 'projects'
  | 'tasks'
  | 'members'
  | 'procurements'
  | 'meetings'
  | 'campus-report'
  | 'demo'
  | 'locations'
  | 'events'
  | 'departments'
  | 'feedback'
  | 'resources'
  | 'social-media'
  | 'vendors'
  | 'store'
  | 'announcements'
  | 'bookings'
  | 'budgets'
  | 'forms'
  | 'rides'
  | 'procedures'
  | 'callcentre'
  | 'salvation'
  | 'assetmanagement'
  | 'qr-codes'
  | 'app-catalogue'
  | 'anatomy'
  | 'ai'
  | 'comms-whatsapp'
  | 'comms-email'
  | 'comms-sms'
  | 'comms-push';

  export interface DomainConfig {
  key: DomainKey;
  label: string;
  description: string;
  icon: ReactNode;
  basePath: string;
  headerActions?: ReactNode;
  menuItems?: DomainMenuItem[];
  menuSections?: DomainMenuSection[];
  // Access control
  requiredRoles?: string[]; // User must have at least one of these roles
  requiredPermissions?: string[]; // User must have at least one of these permissions (rights)
  requireAll?: boolean; // If true, user must have ALL roles/permissions instead of ANY
  strictAccess?: boolean; // If true, Admin users must still have the specific roles (no Admin bypass)
  // If true, exclude from global domain enumerations (home grid, App switcher,
  // quick-switch). The domain still resolves by key for its own sidebar/access —
  // used for sub-apps nested inside another domain (e.g. Communications channels).
  hidden?: boolean;
  // Notifications/Badges
  badge?: string | number; // Badge count or label to show on domain card
  badgeColor?: string; // Badge color (default: red for counts, gray for labels)
}

export interface DomainMenuItem {
  label: string;
  path: string;
  icon?: ReactNode;
  requiredRoles?: string[];
  requiredPermissions?: string[];
  requireAll?: boolean;
  strictAccess?: boolean; // If true, Admin users must still have the specific roles (no Admin bypass)
  badge?: string | number;
  badgeColor?: string;
}

export interface DomainMenuSection {
  label: string;
  icon?: ReactNode;
  items: DomainMenuItem[];
  defaultOpen?: boolean;
}

// Global menu sections for sidebar (not tied to any specific domain)
export const GLOBAL_MENU_SECTIONS: DomainMenuSection[] = [
  {
    label: 'App',
    defaultOpen: true,
    items: [
      { label: 'Announcements', path: '/app/announcements', icon: <IconSpeakerphone size={16} />, requiredRoles: ['Announcements Access'], strictAccess: true },
      { label: 'Bookings', path: '/app/bookings', icon: <IconCalendarEvent size={16} />, requiredRoles: ['Staff', 'BookingAdmin', 'Admin'] },
      { label: 'Budgets', path: '/app/budgets', icon: <IconWallet size={16} />, requiredRoles: ['Admin', 'BudgetManager', 'BudgetAdmin'], strictAccess: true },
      { label: 'Campus Report', path: '/app/campus-report', icon: <IconBuildingChurch size={16} />, requiredRoles: ['CPC Access'], strictAccess: true },
      { label: 'Departments', path: '/app/departments', icon: <IconHierarchy size={16} />, requiredRoles: ['Admin', 'Departments Access'], requiredPermissions: ['Department', 'Ministry'], strictAccess: true },
      { label: 'Events', path: '/app/events', icon: <IconCalendarEvent size={16} />, requiredRoles: ['Admin', 'EventsAdmin', 'Events Access', 'Events Coordinator'], requiredPermissions: ['Event.View'], strictAccess: true },
      { label: 'Feedback', path: '/app/feedback', icon: <IconMessageCircle size={16} />, requiredRoles: ['Feedback Access'], strictAccess: true, badge: 15 },
      { label: 'Forms', path: '/app/forms', icon: <IconForms size={16} />, requiredRoles: ['Admin', 'FormsAdmin', 'FormsManager', 'FormsReviewer', 'FormsViewer'] },
      { label: 'Carpool', path: '/app/rides', icon: <IconCar size={16} />, requiredRoles: ['Admin', 'RidesAdmin', 'RidesSecurity'] },
      { label: 'Procedures', path: '/app/procedures', icon: <IconListCheck size={16} />, requiredRoles: ['Admin', 'ProceduresAdmin', 'ProceduresEditor'] },
      { label: 'Call campaigns', path: '/app/callcentre', icon: <IconPhoneCall size={16} />, requiredRoles: ['Admin', 'CallCentreAdmin', 'CallCentreSupervisor'] },
      { label: 'Salvation Team', path: '/app/salvation', icon: <IconHeartHandshake size={16} />, requiredRoles: ['Admin', 'SalvationAdmin', 'SalvationLeader'], requiredPermissions: ['Salvation.Read'] },
      { label: 'Locations', path: '/app/locations', icon: <IconMapPin size={16} />, requiredRoles: ['Admin', 'LocationsAdmin', 'Locations Access'], strictAccess: true },
      { label: 'Meetings', path: '/app/meetings', icon: <IconCalendar size={16} />, requiredRoles: ['Meetings Access'], strictAccess: true },
      { label: 'HR', path: '/app/members', icon: <IconUsers size={16} />, requiredRoles: ['Members Access', 'Staff'] },
      // Member directory and profiles (Admin role OR the Member.Read right; no strictAccess so Admin always sees it).
      { label: 'Members', path: '/admin/members', icon: <IconAddressBook size={16} />, requiredPermissions: ['Member.Read'] },
      { label: 'Procurements', path: '/app/procurements', icon: <IconShoppingCart size={16} />, requiredRoles: ['ProcurementsAccess'], strictAccess: true, badge: 5 },
      { label: 'Projects', path: '/app/projects', icon: <IconFolders size={16} />, requiredRoles: ['Projects Access'], strictAccess: true },
      { label: 'QR Codes', path: '/app/qr-codes', icon: <IconQrcode size={16} />, requiredRoles: ['Staff'] },
      { label: 'Resources', path: '/app/resources', icon: <IconBooks size={16} />, requiredRoles: ['Resources Access'], strictAccess: true },
      { label: 'Social Media', path: '/app/social-media', icon: <IconBrandFacebook size={16} />, requiredRoles: ['Admin', 'Social Media Access'], strictAccess: true },
      { label: 'Store', path: '/app/store', icon: <IconShoppingBag size={16} />, requiredRoles: ['Admin', 'ShopAdmin'], strictAccess: true },
      { label: 'Tasks', path: '/app/tasks', icon: <IconChecklist size={16} />, requiredRoles: ['Tasks Access'], strictAccess: true, badge: 12 },
      { label: 'Vendors', path: '/app/vendors', icon: <IconBuildingStore size={16} />, requiredRoles: ['Vendors Access'], strictAccess: true, badge: 5 },
    ],
  },
  {
    label: 'User',
    defaultOpen: true,
    items: [
      // No role gate: the page itself only ever shows a user the apps they are entitled
      // to, and the redemption codes assigned to them personally.
      { label: 'Get the Apps', path: '/app/apps', icon: <IconDeviceMobile size={16} /> },
      { label: 'Profile', path: '/profile', icon: <IconUser size={16} /> },
      { label: 'Settings', path: '/settings', icon: <IconSettings size={16} /> },
      { label: 'Connected apps', path: '/settings/connected-apps', icon: <IconPlugConnected size={16} /> },
    ],
  },
  {
    label: 'Admin',
    defaultOpen: false,
    items: [
      { label: 'Users', path: '/admin/users', icon: <IconUsers size={16} />, requiredRoles: ['Admin'], strictAccess: true, badge: 3 },
      // Admin role OR the Member.Read right (no strictAccess, so Admin bypasses the permission check).
      { label: 'Members', path: '/admin/members', icon: <IconAddressBook size={16} />, requiredPermissions: ['Member.Read'] },
      { label: 'Data Deletion Requests', path: '/admin/data-deletion-requests', icon: <IconUserShield size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'Permissions', path: '/admin/permissions', icon: <IconLock size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'App Catalogue', path: '/admin/app-catalogue', icon: <IconApps size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'LLM Connector', path: '/admin/connector', icon: <IconPlugConnected size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'API Keys', path: '/admin/api-keys', icon: <IconKey size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'Reports', path: '/admin/reports', icon: <IconChartBar size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'System Log', path: '/admin/system-log', icon: <IconFileText size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'System', path: '/admin/system', icon: <IconServer size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'Presence Management', path: '/admin/presence', icon: <IconDeviceDesktop size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'Infrastructure', path: '/admin/infrastructure', icon: <IconServer size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'Communications', path: '/admin/communications', icon: <IconSend size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'File Management', path: '/admin/file-management', icon: <IconFolder size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      // In-app help content: Admin role OR the Help.Content.Manage right (no strictAccess, so Admin always sees it).
      { label: 'Help content', path: '/admin/help', icon: <IconHelp size={16} />, requiredPermissions: ['Help.Content.Manage'] },
    ],
  },
];

export const DOMAINS: Record<DomainKey, DomainConfig> = {
  projects: {
    key: 'projects',
    label: 'Projects',
    description: 'Plan and track projects, milestones, and deadlines.',
    icon: <IconFolders size={48} />,
    basePath: '/app/projects',
    requiredRoles: ['Projects Access'],
    strictAccess: true,
    menuItems: [
      { label: 'Overview', path: '/app/projects', icon: <IconLayoutDashboard size={16} /> },
      { label: 'Project list', path: '/app/projects/list', icon: <IconListDetails size={16} /> },
      { label: 'Board', path: '/app/projects/board', icon: <IconListCheck size={16} /> },
      { label: 'Calendar', path: '/app/projects/calendar', icon: <IconCalendar size={16} /> },
      { label: 'My Projects', path: '/app/projects/my-projects', icon: <IconUserCircle size={16} /> },
      { label: 'Reports', path: '/app/projects/reports', icon: <IconChartBar size={16} /> },
      { label: 'Administration', path: '/app/projects/admin', icon: <IconUserShield size={16} />, requiredRoles: ['Admin'], strictAccess: true },
    ],
  },
  tasks: {
    key: 'tasks',
    label: 'Tasks',
    description: 'Manage tasks, assignments, and status.',
    icon: <IconChecklist size={48} />,
    basePath: '/app/tasks',
    requiredRoles: ['Tasks Access'],
    strictAccess: true,
    badge: 12,
    badgeColor: 'red',
    menuItems: [
      { label: 'Overview', path: '/app/tasks', icon: <IconLayoutDashboard size={16} /> },
      { label: 'Board', path: '/app/tasks/board', icon: <IconListCheck size={16} /> },
    ],
  },
  members: {
    key: 'members',
    label: 'HR Management',
    description: 'My profile, leave, files and policies, plus HR admin tools for HR admins.',
    icon: <IconUsers size={48} />,
    basePath: '/app/members',
    requiredRoles: ['Members Access', 'Staff', 'HRAdmin', 'Admin'],
    menuItems: [
      { label: 'Overview', path: '/app/members', icon: <IconLayoutDashboard size={16} /> },
      { label: 'Directory', path: '/app/members/list', icon: <IconListDetails size={16} /> },
    ],
  },
  'qr-codes': {
    key: 'qr-codes',
    label: 'QR Codes',
    description: 'Create, manage, and track QR codes with dynamic redirects and scan analytics.',
    icon: <IconQrcode size={48} />,
    basePath: '/app/qr-codes',
    requiredRoles: ['Staff'],
    menuItems: [
      { label: 'Dashboard', path: '/app/qr-codes', icon: <IconLayoutDashboard size={16} /> },
      { label: 'My QR Codes', path: '/app/qr-codes/my-codes', icon: <IconListDetails size={16} /> },
    ],
  },
  procurements: {
    key: 'procurements',
    label: 'Procurements',
    description: 'Handle procurement requests and approvals.',
    icon: <IconShoppingCart size={48} />,
    basePath: '/app/procurements',
    requiredRoles: ['Procurements Access'],
    strictAccess: true,
    badge: 5,
    badgeColor: 'orange',
    menuItems: [
      { label: 'Overview', path: '/app/procurements', icon: <IconLayoutDashboard size={16} /> },
    ],
  },
  meetings: {
    key: 'meetings',
    label: 'Meetings',
    description: 'Schedule and manage meetings.',
    icon: <IconCalendar size={48} />,
    basePath: '/app/meetings',
    requiredRoles: ['Meetings Access'],
    strictAccess: true,
    menuItems: [
      { label: 'Overview', path: '/app/meetings', icon: <IconLayoutDashboard size={16} /> },
    ],
  },
  'campus-report': {
    key: 'campus-report',
    label: 'Campus Report',
    description: 'View reports and metrics for all campuses.',
    icon: <IconBuildingChurch size={48} />,
    basePath: '/app/campus-report',
    requiredRoles: ['CPC Access'],
    strictAccess: true,
    menuItems: [
      { label: 'Dashboard', path: '/app/campus-report', icon: <IconLayoutDashboard size={16} /> },
    ],
  },
  demo: {
    key: 'demo',
    label: 'Demo Components',
    description: 'Explore UI components, themes, and features.',
    icon: <IconComponents size={48} />,
    basePath: '/app/demo',
    requiredRoles: ['Developers'],
    strictAccess: true,
    // Menu is defined in domain/providers/demoMenu.tsx (Menu Domain Contract)
    menuSections: [
  //     {
  //       label: 'Overview',
  //       icon: <IconLayoutDashboard size={14} />,
  //       defaultOpen: true,
  //       items: [
  //         { label: 'Overview', path: '/app/demo', icon: <IconLayoutDashboard size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'App',
  //       icon: <IconApps size={14} />,
  //       defaultOpen: true,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/dashboard', icon: <IconLayoutGrid size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Apps',
  //       icon: <IconApps size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'All Apps', path: '/app/demo/apps', icon: <IconLayoutGrid size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'E-Commerce',
  //       icon: <IconShoppingCart size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/ecommerce', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Products', path: '/app/demo/apps/ecommerce/products', icon: <IconShoppingCart size={16} />, badge: 24 },
  //         { label: 'Orders', path: '/app/demo/apps/ecommerce/orders', icon: <IconListDetails size={16} />, badge: 156, badgeColor: 'green' },
  //         { label: 'Customers', path: '/app/demo/apps/ecommerce/customers', icon: <IconUsers size={16} />, badge: 89 },
  //       ],
  //     },
  //     {
  //       label: 'Project Management',
  //       icon: <IconFolders size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/project-management', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'View Projects', path: '/app/demo/apps/project-management/projects', icon: <IconFolders size={16} />, badge: 12 },
  //         { label: 'View Board', path: '/app/demo/apps/project-management/board', icon: <IconListCheck size={16} />, badge: 8, badgeColor: 'orange' },
  //         { label: 'View Team', path: '/app/demo/apps/project-management/team', icon: <IconUsers size={16} />, badge: 24 },
  //       ],
  //     },
  //     {
  //       label: 'CRM',
  //       icon: <IconUsers size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/crm', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Pipeline', path: '/app/demo/apps/crm/pipeline', icon: <IconListCheck size={16} />, badge: '$2.4M', badgeColor: 'green' },
  //         { label: 'Contacts', path: '/app/demo/apps/crm/contacts', icon: <IconUsers size={16} />, badge: 156 },
  //         { label: 'Activities', path: '/app/demo/apps/crm/activities', icon: <IconChecklist size={16} />, badge: 23, badgeColor: 'orange' },
  //       ],
  //     },
  //     {
  //       label: 'HR Portal',
  //       icon: <IconUserCircle size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/hr-portal', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Directory', path: '/app/demo/apps/hr-portal/directory', icon: <IconUsers size={16} />, badge: 248 },
  //         { label: 'Organization', path: '/app/demo/apps/hr-portal/organization', icon: <IconFolders size={16} />, badge: 12 },
  //         { label: 'Leave Management', path: '/app/demo/apps/hr-portal/leave', icon: <IconCalendar size={16} />, badge: 5, badgeColor: 'red' },
  //       ],
  //     },
  //     {
  //       label: 'File Manager',
  //       icon: <IconFolder size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/file-manager', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'My Files', path: '/app/demo/apps/file-manager/my-files', icon: <IconFolder size={16} />, badge: 128 },
  //         { label: 'Shared', path: '/app/demo/apps/file-manager/shared', icon: <IconUsers size={16} />, badge: 45 },
  //         { label: 'Trash', path: '/app/demo/apps/file-manager/trash', icon: <IconChecklist size={16} />, badge: 8, badgeColor: 'gray' },
  //       ],
  //     },
  //     {
  //       label: 'Analytics',
  //       icon: <IconChartBar size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/analytics', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Reports', path: '/app/demo/apps/analytics/reports', icon: <IconListDetails size={16} />, badge: 15 },
  //         { label: 'Audience', path: '/app/demo/apps/analytics/audience', icon: <IconUsers size={16} />, badge: '24.5K' },
  //         { label: 'Exports', path: '/app/demo/apps/analytics/exports', icon: <IconDatabase size={16} />, badge: 3, badgeColor: 'cyan' },
  //       ],
  //     },
  //     {
  //       label: 'Chat',
  //       icon: <IconMessage size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Messages', path: '/app/demo/apps/chat', icon: <IconMessage size={16} />, badge: 12, badgeColor: 'red' },
  //         { label: 'Contacts', path: '/app/demo/apps/chat/contacts', icon: <IconUsers size={16} />, badge: 86 },
  //         { label: 'Groups', path: '/app/demo/apps/chat/groups', icon: <IconUsers size={16} />, badge: 8 },
  //         { label: 'Settings', path: '/app/demo/apps/chat/settings', icon: <IconSettings size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Calendar',
  //       icon: <IconCalendar size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Calendar', path: '/app/demo/apps/calendar', icon: <IconCalendar size={16} /> },
  //         { label: 'Events', path: '/app/demo/apps/calendar/events', icon: <IconListDetails size={16} />, badge: 28 },
  //         { label: 'Schedule', path: '/app/demo/apps/calendar/schedule', icon: <IconChecklist size={16} />, badge: 5, badgeColor: 'violet' },
  //         { label: 'Meeting Rooms', path: '/app/demo/apps/calendar/rooms', icon: <IconFolders size={16} />, badge: 6 },
  //       ],
  //     },
  //     {
  //       label: 'Invoicing',
  //       icon: <IconReceipt size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/invoicing', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Invoices', path: '/app/demo/apps/invoicing/invoices', icon: <IconReceipt size={16} />, badge: 42 },
  //         { label: 'Payments', path: '/app/demo/apps/invoicing/payments', icon: <IconShoppingCart size={16} />, badge: 8, badgeColor: 'yellow' },
  //         { label: 'Clients', path: '/app/demo/apps/invoicing/clients', icon: <IconUsers size={16} />, badge: 67 },
  //       ],
  //     },
  //     {
  //       label: 'Helpdesk',
  //       icon: <IconHeadset size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/helpdesk', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Tickets', path: '/app/demo/apps/helpdesk/tickets', icon: <IconListDetails size={16} />, badge: 23, badgeColor: 'red' },
  //         { label: 'Knowledge Base', path: '/app/demo/apps/helpdesk/knowledge', icon: <IconDatabase size={16} />, badge: 156 },
  //         { label: 'Reports', path: '/app/demo/apps/helpdesk/reports', icon: <IconChartBar size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'UI Components',
  //       icon: <IconComponents size={14} />,
  //       defaultOpen: true,
  //       items: [
  //         { label: 'Core', path: '/app/demo/ui/core', icon: <IconSquare size={16} /> },
  //         { label: 'Inputs', path: '/app/demo/ui/inputs', icon: <IconForms size={16} /> },
  //         { label: 'Data Display', path: '/app/demo/ui/data-display', icon: <IconDatabase size={16} /> },
  //         { label: 'Feedback', path: '/app/demo/ui/feedback', icon: <IconSparkles size={16} /> },
  //         { label: 'Alerts', path: '/app/demo/ui/alerts', icon: <IconBell size={16} /> },
  //         { label: 'Navigation', path: '/app/demo/ui/navigation', icon: <IconListCheck size={16} /> },
  //         { label: 'Overlays', path: '/app/demo/ui/overlays', icon: <IconClick size={16} /> },
  //         { label: 'Layout', path: '/app/demo/ui/layout', icon: <IconTable size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Advanced',
  //       icon: <IconBriefcase size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Calendar', path: '/app/demo/advanced/calendar', icon: <IconCalendar size={16} /> },
  //         { label: 'Workflow', path: '/app/demo/advanced/workflow', icon: <IconChecklist size={16} /> },
  //         { label: 'Editor', path: '/app/demo/advanced/editor', icon: <IconEdit size={16} /> },
  //         { label: 'Data Tables', path: '/app/demo/advanced/data-tables', icon: <IconTable size={16} /> },
  //         { label: 'Form Builder', path: '/app/demo/advanced/form-builder', icon: <IconForms size={16} />, badge: 'New', badgeColor: 'green' },
  //         { label: 'KPI Widgets', path: '/app/demo/advanced/widgets/kpi', icon: <IconChartBar size={16} /> },
  //         { label: 'Line Chart Widgets', path: '/app/demo/advanced/widgets/line-chart', icon: <IconChartLine size={16} /> },
  //         { label: 'Bar Chart Widgets', path: '/app/demo/advanced/widgets/bar-chart', icon: <IconChartBar size={16} /> },
  //         { label: 'Pie Chart Widgets', path: '/app/demo/advanced/widgets/pie-chart', icon: <IconChartPie size={16} /> },
  //         { label: 'Area Chart Widgets', path: '/app/demo/advanced/widgets/area-chart', icon: <IconChartArea size={16} /> },
  //         { label: 'Data Table Widgets', path: '/app/demo/advanced/widgets/data-table', icon: <IconTableOptions size={16} /> },
  //         { label: 'Gauge Widgets', path: '/app/demo/advanced/widgets/gauges', icon: <IconGauge size={16} /> },
  //         { label: 'Heatmap Widgets', path: '/app/demo/advanced/widgets/heatmaps', icon: <IconChartDots size={16} /> },
  //         { label: 'Alert Widgets', path: '/app/demo/advanced/widgets/alerts', icon: <IconAlertCircle size={16} /> },
  //         { label: 'AI Insight Widgets', path: '/app/demo/advanced/widgets/ai-insights', icon: <IconBrain size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Animations',
  //       icon: <IconSparkles size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Transitions', path: '/app/demo/animations/transitions', icon: <IconSparkles size={16} /> },
  //         { label: 'Interactive', path: '/app/demo/animations/interactive', icon: <IconClick size={16} /> },
  //         { label: 'Scroll Effects', path: '/app/demo/animations/scroll', icon: <IconArrowsMove size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Domain Examples',
  //       icon: <IconFolders size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Projects', path: '/app/demo/domains/projects', icon: <IconFolders size={16} /> },
  //         { label: 'Tasks', path: '/app/demo/domains/tasks', icon: <IconChecklist size={16} /> },
  //         { label: 'Members', path: '/app/demo/domains/members', icon: <IconUsers size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Settings & Theming',
  //       icon: <IconSettings size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Color Schemes', path: '/app/demo/settings/colors', icon: <IconPalette size={16} /> },
  //         { label: 'Theme Mode', path: '/app/demo/settings/theme', icon: <IconMoon size={16} /> },
  //         { label: 'Typography', path: '/app/demo/settings/typography', icon: <IconTypography size={16} /> },
  //         { label: 'Translations', path: '/app/demo/settings/translations', icon: <IconLanguage size={16} /> },
  //         { label: 'Accessibility', path: '/app/demo/settings/accessibility', icon: <IconAccessible size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Auth Pages',
  //       icon: <IconLock size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Overview', path: '/app/demo/auth-pages', icon: <IconLayoutGrid size={16} /> },
  //         { label: 'Login', path: '/app/demo/auth-pages/login', icon: <IconLogin size={16} />, badge: 17 },
  //         { label: 'Registration', path: '/app/demo/auth-pages/register', icon: <IconUserPlus size={16} />, badge: 17 },
  //         { label: 'Forgot Password', path: '/app/demo/auth-pages/forgot-password', icon: <IconKey size={16} />, badge: 17 },
  //         { label: 'Reset Password', path: '/app/demo/auth-pages/reset-password', icon: <IconLockCheck size={16} />, badge: 17 },
  //         { label: 'OTP / 2FA', path: '/app/demo/auth-pages/otp', icon: <IconDeviceMobile size={16} />, badge: 17 },
  //         { label: 'Other', path: '/app/demo/auth-pages/other', icon: <IconDots size={16} />, badge: 5 },
  //       ],
  //     },
    ],
  },
  locations: {
    key: 'locations',
    label: 'Locations',
    description: 'Campuses with their addresses, service times, venues, leaders and social media.',
    icon: <IconMapPin size={48} />,
    basePath: '/app/locations',
    // Admin listed explicitly: strictAccess disables the Admin bypass. LocationsAdmin (all rights) and
    // "Locations Access" (read rights) come from migration 261; a per-user Location.View right also opens it.
    requiredRoles: ['Admin', 'LocationsAdmin', 'Locations Access'],
    requiredPermissions: ['Location.View'],
    strictAccess: true,
    menuItems: [
      { label: 'Dashboard', path: '/app/locations', icon: <IconLayoutDashboard size={16} />, requiredPermissions: ['Location.View'] },
      { label: 'Campuses', path: '/app/locations/campuses', icon: <IconBuildingChurch size={16} />, requiredPermissions: ['Location.View'] },
      { label: 'Service schedule', path: '/app/locations/service-schedule', icon: <IconCalendar size={16} />, requiredPermissions: ['Location.ServiceTime.View'] },
      { label: 'Venues', path: '/app/locations/venues', icon: <IconMapPin size={16} />, requiredPermissions: ['Location.Venue.View'] },
      { label: 'Leaders', path: '/app/locations/leaders', icon: <IconUsers size={16} />, requiredPermissions: ['Location.Leader.View'] },
      { label: 'Social media platforms', path: '/app/locations/social-media', icon: <IconBrandFacebook size={16} />, requiredPermissions: ['Location.SocialMedia.View'] },
    ],
  },
  events: {
    key: 'events',
    label: 'Events',
    description: 'The church calendar: events from CCMS and Planning Center, review, mappings and event types.',
    icon: <IconCalendarEvent size={48} />,
    basePath: '/app/events',
    // Admin listed explicitly: strictAccess disables the Admin bypass. EventsAdmin (all rights) and
    // "Events Access" (read rights) come from PG identity/004; "Events Coordinator" (Event.View and
    // Event.Link.Manage) from identity/005; a per-user Event.View right also opens it.
    requiredRoles: ['Admin', 'EventsAdmin', 'Events Access', 'Events Coordinator'],
    requiredPermissions: ['Event.View'],
    strictAccess: true,
    menuItems: [
      { label: 'Dashboard', path: '/app/events', icon: <IconLayoutDashboard size={16} />, requiredPermissions: ['Event.View'] },
      { label: 'Events', path: '/app/events/events', icon: <IconCalendarEvent size={16} />, requiredPermissions: ['Event.View'] },
      { label: 'Review', path: '/app/events/review', icon: <IconChecklist size={16} />, requiredPermissions: ['Event.Review'] },
      { label: 'Sources', path: '/app/events/sources', icon: <IconPlugConnected size={16} />, requiredPermissions: ['Event.Source.View'] },
      { label: 'Mappings', path: '/app/events/mappings', icon: <IconArrowsExchange size={16} />, requiredPermissions: ['Event.Source.View'] },
      { label: 'Rules', path: '/app/events/rules', icon: <IconBolt size={16} />, requiredPermissions: ['Event.Rule.Manage'] },
      { label: 'Learning', path: '/app/events/learning', icon: <IconBulb size={16} />, requiredPermissions: ['Event.Learning.Review'] },
      { label: 'Tags', path: '/app/events/tags', icon: <IconTags size={16} />, requiredPermissions: ['Event.Tag.Manage'] },
      { label: 'Playbooks', path: '/app/events/playbooks', icon: <IconChecklist size={16} />, requiredPermissions: ['Event.Playbook.Manage'] },
      { label: 'Event types', path: '/app/events/types', icon: <IconCategory size={16} />, requiredPermissions: ['Event.Type.View'] },
    ],
  },
  departments: {
    key: 'departments',
    label: 'Departments',
    description: 'Organize and manage church departments.',
    icon: <IconHierarchy size={48} />,
    basePath: '/app/departments',
    // Role OR right: Admin / "Departments Access", or the Department / Ministry right on its own.
    requiredRoles: ['Admin', 'Departments Access'],
    requiredPermissions: ['Department', 'Ministry'],
    strictAccess: true,
    menuItems: [],
  },
  feedback: {
    key: 'feedback',
    label: 'Feedback',
    description: 'Collect and manage member feedback.',
    icon: <IconMessageCircle size={48} />,
    basePath: '/app/feedback',
    requiredRoles: ['Feedback Access'],
    strictAccess: true,
    badge: 15,
    badgeColor: 'blue',
    menuItems: [],
  },
  resources: {
    key: 'resources',
    label: 'Resources',
    description: 'Library of books, videos, audio, and documents.',
    icon: <IconBooks size={48} />,
    basePath: '/app/resources',
    requiredRoles: ['Resources Access'],
    strictAccess: true,
    menuItems: [],
  },
  'anatomy': {
    key: 'anatomy',
    label: 'Anatomy',
    description: 'Send follow-ups and meeting registrations to the Anatomy church platform.',
    icon: <IconPlugConnected size={48} />,
    basePath: '/app/anatomy',
    // Admin listed explicitly: strictAccess disables the Admin bypass. AnatomyAccess is seeded by
    // migration 246 for the people who work the follow-up / meeting desk.
    requiredRoles: ['Admin', 'AnatomyAccess'],
    strictAccess: true,
    menuItems: [],
  },
  'social-media': {
    key: 'social-media',
    label: 'Social Media',
    description: 'Manage social media presence and engagement.',
    icon: <IconBrandFacebook size={48} />,
    basePath: '/app/social-media',
    // 'Admin' is listed explicitly because strictAccess disables the Admin bypass in
    // useDomainAccess. 'Social Media Access' is not seeded or issued anywhere, so
    // without Admin here the domain is unreachable by every user. Matches the
    // ['Admin', 'ShopAdmin'] shape used by the store domain.
    requiredRoles: ['Admin', 'Social Media Access'],
    strictAccess: true,
    menuItems: [],
  },
  vendors: {
    key: 'vendors',
    label: 'Vendors',
    description: 'Manage vendor relationships and contracts.',
    icon: <IconBuildingStore size={48} />,
    basePath: '/app/vendors',
    requiredRoles: ['Vendors Access'],
    strictAccess: true,
    badge: 5,
    badgeColor: 'orange',
    menuItems: [],
  },
  announcements: {
    key: 'announcements',
    label: 'Announcements',
    description: 'Create and manage announcements across locations.',
    icon: <IconSpeakerphone size={48} />,
    basePath: '/app/announcements',
    requiredRoles: ['Announcements Access'],
    strictAccess: true,
    menuItems: [],
  },
  store: {
    key: 'store',
    label: 'Store',
    description: 'Products, orders, payments, and customers.',
    icon: <IconShoppingBag size={48} />,
    basePath: '/app/store',
    requiredRoles: ['Admin', 'ShopAdmin'],
    strictAccess: true,
    menuItems: [],
  },
  bookings: {
    key: 'bookings',
    label: 'Bookings',
    description: 'Book and manage physical resources: cars, phones, equipment and rooms.',
    icon: <IconCalendarEvent size={48} />,
    basePath: '/app/bookings',
    // Staff reach the self-service portal; BookingAdmin/Admin additionally
    // unlock the admin sections. Listing all three here ensures a BookingAdmin
    // who isn't also flagged Staff is never blocked at the domain gate —
    // item-level visibility (see domain/providers/bookingsMenu.tsx) decides
    // what each role actually sees inside.
    requiredRoles: ['Staff', 'BookingAdmin', 'Admin'],
    strictAccess: false,
    menuItems: [
      { label: 'Dashboard', path: '/app/bookings', icon: <IconLayoutDashboard size={16} /> },
      { label: 'My Bookings', path: '/app/bookings/my-bookings', icon: <IconListDetails size={16} /> },
      { label: 'My Licence', path: '/app/bookings/my-licences', icon: <IconListDetails size={16} /> },
      { label: 'Calendar', path: '/app/bookings/calendar', icon: <IconCalendar size={16} /> },
      { label: 'Overview', path: '/app/bookings/admin/overview', icon: <IconLayoutDashboard size={16} />, requiredRoles: ['BookingAdmin', 'Admin'] },
      { label: 'All Bookings', path: '/app/bookings/all', icon: <IconCalendarEvent size={16} />, requiredRoles: ['BookingAdmin', 'Admin'] },
      { label: 'Resources', path: '/app/bookings/resources', icon: <IconBox size={16} />, requiredRoles: ['BookingAdmin', 'Admin'] },
      { label: 'Settings', path: '/app/bookings/settings', icon: <IconSettings size={16} />, requiredRoles: ['BookingAdmin', 'Admin'] },
      { label: 'Reports', path: '/app/bookings/reports', icon: <IconChartBar size={16} />, requiredRoles: ['BookingAdmin', 'Admin'] },
    ],
  },
  budgets: {
    key: 'budgets',
    label: 'Budgets',
    description: 'Advisory organisation-wide budgeting: plan, approve and monitor department, project and donation budgets against actual spend.',
    icon: <IconWallet size={48} />,
    basePath: '/app/budgets',
    requiredRoles: ['Admin', 'BudgetManager', 'BudgetAdmin'],
    strictAccess: true,
    menuItems: [
      // Overview
      { label: 'Dashboard', path: '/app/budgets', icon: <IconLayoutDashboard size={16} /> },
      // Budgets
      { label: 'All Budgets', path: '/app/budgets/list', icon: <IconListDetails size={16} /> },
      { label: 'Create Budget', path: '/app/budgets/create', icon: <IconEdit size={16} /> },
      // Approvals (approver-gated)
      { label: 'Approval Queue', path: '/app/budgets/approvals', icon: <IconChecklist size={16} />, requiredRoles: ['Admin', 'BudgetAdmin'], strictAccess: true },
      // Monitoring (approver-gated)
      { label: 'Alerts', path: '/app/budgets/alerts', icon: <IconBell size={16} />, requiredRoles: ['Admin', 'BudgetAdmin'], strictAccess: true },
      { label: 'Unbudgeted Spend', path: '/app/budgets/unbudgeted', icon: <IconReceipt size={16} />, requiredRoles: ['Admin', 'BudgetAdmin'], strictAccess: true },
      // Administration
      { label: 'Settings & Thresholds', path: '/app/budgets/admin/settings', icon: <IconSettings size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'Lookups', path: '/app/budgets/admin/lookups', icon: <IconDatabase size={16} />, requiredRoles: ['Admin'], strictAccess: true },
    ],
  },
  forms: {
    key: 'forms',
    label: 'Forms',
    description: 'Build registration, sign-up and survey forms. Share by link or QR, take payments, manage capacity and turn entries into members.',
    icon: <IconForms size={48} />,
    basePath: '/app/forms',
    requiredRoles: ['Admin', 'FormsAdmin', 'FormsManager', 'FormsReviewer', 'FormsViewer'],
    strictAccess: false,
    menuItems: [
      { label: 'Dashboard', path: '/app/forms', icon: <IconLayoutDashboard size={16} /> },
      { label: 'All Forms', path: '/app/forms/forms', icon: <IconListDetails size={16} /> },
      { label: 'Create Form', path: '/app/forms/forms/create', icon: <IconEdit size={16} />, requiredRoles: ['Admin', 'FormsAdmin', 'FormsManager'] },
      { label: 'Approvals', path: '/app/forms/approvals', icon: <IconChecklist size={16} />, requiredRoles: ['Admin', 'FormsAdmin', 'FormsManager', 'FormsReviewer'] },
      { label: 'Webhooks', path: '/app/forms/webhooks', icon: <IconWebhook size={16} />, requiredRoles: ['Admin', 'FormsAdmin'] },
      { label: 'Settings', path: '/app/forms/admin/settings', icon: <IconSettings size={16} />, requiredRoles: ['Admin', 'FormsAdmin'] },
      { label: 'Lookups', path: '/app/forms/admin/lookups', icon: <IconCategory size={16} />, requiredRoles: ['Admin', 'FormsAdmin'] },
    ],
  },
  rides: {
    key: 'rides',
    label: 'Carpool',
    description: 'Verified church carpooling: drivers, document checks, bookings, campus security checks and the ride calendar.',
    icon: <IconCar size={48} />,
    basePath: '/app/rides',
    requiredRoles: ['Admin', 'RidesAdmin', 'RidesSecurity'],
    strictAccess: false,
    menuItems: [
      { label: 'Dashboard', path: '/app/rides', icon: <IconLayoutDashboard size={16} />, requiredRoles: ['Admin', 'RidesAdmin'] },
      { label: 'Drivers', path: '/app/rides/drivers', icon: <IconUsers size={16} />, requiredRoles: ['Admin', 'RidesAdmin'] },
      { label: 'Verification queue', path: '/app/rides/verifications', icon: <IconChecklist size={16} /> },
      { label: 'Bookings', path: '/app/rides/bookings', icon: <IconCalendarEvent size={16} />, requiredRoles: ['Admin', 'RidesAdmin'] },
      { label: 'Security desk', path: '/app/rides/security', icon: <IconLock size={16} /> },
      { label: 'Campuses', path: '/app/rides/campuses', icon: <IconMapPin size={16} />, requiredRoles: ['Admin', 'RidesAdmin'] },
      { label: 'Events', path: '/app/rides/events', icon: <IconCalendar size={16} />, requiredRoles: ['Admin', 'RidesAdmin'] },
      { label: 'Settings', path: '/app/rides/admin/settings', icon: <IconSettings size={16} />, requiredRoles: ['Admin', 'RidesAdmin'] },
      { label: 'Lookups', path: '/app/rides/admin/lookups', icon: <IconDatabase size={16} />, requiredRoles: ['Admin', 'RidesAdmin'] },
    ],
  },
  procedures: {
    key: 'procedures',
    label: 'Procedures',
    description: 'Step-by-step operating procedures with photos, checks and warnings. Let AI draft them from a document, share them by QR code and see who ran what.',
    icon: <IconListCheck size={48} />,
    basePath: '/app/procedures',
    requiredRoles: ['Admin', 'ProceduresAdmin', 'ProceduresEditor'],
    strictAccess: false,
    menuItems: [
      { label: 'Dashboard', path: '/app/procedures', icon: <IconLayoutDashboard size={16} /> },
      { label: 'All procedures', path: '/app/procedures/list', icon: <IconListDetails size={16} /> },
      { label: 'Import with AI', path: '/app/procedures/import', icon: <IconFileImport size={16} /> },
      { label: 'Runs', path: '/app/procedures/runs', icon: <IconChecklist size={16} /> },
      { label: 'Problems', path: '/app/procedures/problems', icon: <IconFlag size={16} /> },
      { label: 'QR sheet', path: '/app/procedures/qr-sheet', icon: <IconQrcode size={16} /> },
      { label: 'Categories', path: '/app/procedures/categories', icon: <IconCategory size={16} /> },
      { label: 'Settings', path: '/app/procedures/settings', icon: <IconSettings size={16} />, requiredRoles: ['Admin', 'ProceduresAdmin'] },
    ],
  },
  // Call Centre Management (CCMS.Api.CallCentreManagement, :5048). Labelled "Call campaigns" so it is not
  // confused with Communications campaigns. Volunteers call from the separate caller portal.
  callcentre: {
    key: 'callcentre',
    label: 'Call campaigns',
    description: 'The organisation\'s call centre: plan calling campaigns, add lists of people, match them to CCMS members, share them out to volunteer callers and follow progress, journey badges and handoffs.',
    icon: <IconPhoneCall size={48} />,
    basePath: '/app/callcentre',
    requiredRoles: ['Admin', 'CallCentreAdmin', 'CallCentreSupervisor'],
    strictAccess: false,
    menuItems: [
      { label: 'Dashboard', path: '/app/callcentre', icon: <IconLayoutDashboard size={16} /> },
      { label: 'Campaigns', path: '/app/callcentre/campaigns', icon: <IconPhoneCall size={16} /> },
      { label: 'Contacts', path: '/app/callcentre/contacts', icon: <IconAddressBook size={16} /> },
      { label: 'Do not call', path: '/app/callcentre/do-not-call', icon: <IconPhoneOff size={16} /> },
      { label: 'Detail changes', path: '/app/callcentre/detail-changes', icon: <IconListDetails size={16} /> },
      { label: 'Journey', path: '/app/callcentre/journey', icon: <IconRoute size={16} /> },
      { label: 'Handoffs', path: '/app/callcentre/handoffs', icon: <IconArrowForwardUp size={16} /> },
      { label: 'Callers', path: '/app/callcentre/callers', icon: <IconUsers size={16} /> },
      { label: 'Reports', path: '/app/callcentre/reports', icon: <IconChartBar size={16} /> },
      { label: 'Settings', path: '/app/callcentre/settings', icon: <IconSettings size={16} />, requiredRoles: ['Admin', 'CallCentreAdmin'] },
    ],
  },
  // Salvation Management (CCMS.Api.SalvationManagement, :5049): the Salvation Team. Leaders manage people who gave
  // their lives to Jesus here; volunteers follow up from the separate portal (salvation.actschurch.co.za).
  salvation: {
    key: 'salvation',
    label: 'Salvation Team',
    description: 'Every person who gives their life to Jesus gets a permanent Lead and Backup volunteer: follow up before the weekly deadline, meet them at church and walk with them until they are connected.',
    icon: <IconHeartHandshake size={48} />,
    basePath: '/app/salvation',
    requiredRoles: ['Admin', 'SalvationAdmin', 'SalvationLeader'],
    requiredPermissions: ['Salvation.Read'],
    strictAccess: false,
    menuItems: [
      { label: 'Overview', path: '/app/salvation', icon: <IconLayoutDashboard size={16} /> },
      { label: 'People', path: '/app/salvation/people', icon: <IconUsers size={16} /> },
      { label: 'Campus teams', path: '/app/salvation/teams', icon: <IconUsersGroup size={16} /> },
      { label: 'Settings', path: '/app/salvation/settings', icon: <IconSettings size={16} />, requiredRoles: ['Admin', 'SalvationAdmin'] },
    ],
  },
  assetmanagement: {
    key: 'assetmanagement',
    label: 'Asset Management',
    description: 'Register, track, transfer and value organisational assets, with RFID scan-to-find.',
    icon: <IconBox size={48} />,
    basePath: '/app/assetmanagement',
    // Staff/AssetReader get the self-service surface; admin items below carry their own roles.
    requiredRoles: ['Staff', 'AssetReader', 'AssetAdmin', 'Admin'],
    strictAccess: false,
    menuItems: [
      // Staff (all asset users)
      { label: 'Dashboard', path: '/app/assetmanagement', icon: <IconLayoutDashboard size={16} /> },
      { label: 'Scan to Find', path: '/app/assetmanagement/scan', icon: <IconScan size={16} /> },
      { label: 'My Assets', path: '/app/assetmanagement/my-assets', icon: <IconClipboardList size={16} /> },
      // Admin (AssetAdmin / Admin)
      { label: 'Overview', path: '/app/assetmanagement/admin/overview', icon: <IconGauge size={16} />, requiredRoles: ['AssetAdmin', 'Admin'] },
      { label: 'All Assets', path: '/app/assetmanagement/list', icon: <IconBox size={16} />, requiredRoles: ['AssetAdmin', 'Admin'] },
      { label: 'Transfers', path: '/app/assetmanagement/admin/transfers', icon: <IconArrowsExchange size={16} />, requiredRoles: ['AssetAdmin', 'Admin'] },
      { label: 'Import Assets', path: '/app/assetmanagement/bulk-import', icon: <IconFileImport size={16} />, requiredRoles: ['AssetAdmin', 'Admin'] },
      { label: 'Reports', path: '/app/assetmanagement/admin/reports', icon: <IconReportAnalytics size={16} />, requiredRoles: ['AssetAdmin', 'Admin'] },
      { label: 'Lookups', path: '/app/assetmanagement/admin/lookups', icon: <IconDatabase size={16} />, requiredRoles: ['AssetAdmin', 'Admin'] },
      { label: 'Settings', path: '/app/assetmanagement/admin/settings', icon: <IconSettings size={16} />, requiredRoles: ['AssetAdmin', 'Admin'] },
    ],
  },
  'app-catalogue': {
    key: 'app-catalogue',
    label: 'App Catalogue',
    description: 'Manage the CCMS mobile constellation: Hub and spoke app manifests, entitlements and recommendations.',
    icon: <IconApps size={48} />,
    basePath: '/admin/app-catalogue',
    requiredRoles: ['Admin'],
    strictAccess: true,
    menuItems: [
      { label: 'Overview', path: '/admin/app-catalogue', icon: <IconLayoutDashboard size={16} /> },
      { label: 'Apps', path: '/admin/app-catalogue/apps', icon: <IconApps size={16} /> },
      { label: 'Recommendations', path: '/admin/app-catalogue/recommendations', icon: <IconSparkles size={16} /> },
      { label: 'Entitlement Preview', path: '/admin/app-catalogue/entitlements', icon: <IconShieldCheck size={16} /> },
      { label: 'Features & Permissions', path: '/admin/app-catalogue/features', icon: <IconFlag size={16} /> },
    ],
  },

  // ---- AI Management ------------------------------------------------------
  // AI is a domain-app spanning /app/ai (features) and /admin/ai (settings),
  // both wrapped in MainLayout domainKey="ai". Its left sidebar comes from the
  // per-domain provider in src/domain/providers/aiMenu.tsx, so menuItems is
  // intentionally empty here (same menu-from-provider pattern as demo/comms).
  // `hidden: true` keeps it OUT of the global grid/switcher because the home
  // screen already surfaces "AI Management" via a dedicated LLM-Admin tile
  // (see src/pages/index.tsx) — without this the tile would appear twice.
  ai: {
    key: 'ai',
    label: 'AI Management',
    description: 'AI workflows, content generation, document Q&A, feature flags, presets, and usage analytics.',
    icon: <IconBrain size={48} />,
    basePath: '/app/ai',
    requiredRoles: ['LLM Admin', 'Admin'],
    strictAccess: true,
    hidden: true,
    menuItems: [],
  },

  // ---- Communications channels --------------------------------------------
  // Each channel is a domain-app (its own left sidebar via its menu provider)
  // but `hidden: true` keeps it OUT of the global domain switcher/grid — the
  // single top-level entry stays "Communications", and channels are reached by
  // navigating into the Communications hub. The real menu comes from the
  // per-channel provider in src/domain/providers/comms*Menu.tsx, so menuItems
  // is intentionally empty here (same pattern as demo/ai).
  'comms-whatsapp': {
    key: 'comms-whatsapp',
    label: 'WhatsApp',
    description: 'WhatsApp two-way messaging, templates, and campaigns via 360dialog.',
    icon: <IconBrandWhatsapp size={48} />,
    basePath: '/admin/communications/whatsapp',
    requiredRoles: ['Admin'],
    strictAccess: true,
    hidden: true,
    menuItems: [],
  },
  'comms-email': {
    key: 'comms-email',
    label: 'Email',
    description: 'Email messaging, templates, campaigns, and delivery tracking.',
    icon: <IconMail size={48} />,
    basePath: '/admin/communications/email',
    requiredRoles: ['Admin'],
    strictAccess: true,
    hidden: true,
    menuItems: [],
  },
  'comms-sms': {
    key: 'comms-sms',
    label: 'SMS',
    description: 'SMS messaging, templates, and campaigns.',
    icon: <IconMessage size={48} />,
    basePath: '/admin/communications/sms',
    requiredRoles: ['Admin'],
    strictAccess: true,
    hidden: true,
    menuItems: [],
  },
  'comms-push': {
    key: 'comms-push',
    label: 'Push Notifications',
    description: 'Push notifications, templates, and campaigns across Expo, Firebase, and APNs.',
    icon: <IconBell size={48} />,
    basePath: '/admin/communications/push',
    requiredRoles: ['Admin'],
    strictAccess: true,
    hidden: true,
    menuItems: [],
  },
};
